import { useState, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const API = process.env.REACT_APP_BACKEND_ADDRESS;

function formatTimestamp(dateStr, editedAt) {
    const date = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z'));
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);

    let relative;
    if (diffMs < 60000) {
        relative = 'just now';
    } else if (diffMs < 3600000) {
        const mins = Math.floor(diffMs / 60000);
        relative = `${mins}m ago`;
    } else if (diffHours < 24) {
        const hrs = Math.floor(diffHours);
        relative = `${hrs}h ago`;
    } else {
        relative = null;
    }

    const absolute = date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
    });

    const edited = editedAt ? ' (edited)' : '';

    if (relative) {
        return { display: relative + edited, tooltip: absolute };
    }
    return { display: absolute + edited, tooltip: null };
}

function authorName(author) {
    return author.display_name || `${author.first_name} ${author.last_name}`;
}

function authorInitials(author) {
    const name = authorName(author);
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function CommentInput({ placeholder, onSubmit, onCancel, autoFocus = false, initialText = '' }) {
    const [text, setText] = useState(initialText);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!text.trim()) return;
        setSubmitting(true);
        await onSubmit(text.trim());
        setText('');
        setSubmitting(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
                multiline
                minRows={2}
                maxRows={6}
                fullWidth
                size="small"
                placeholder={placeholder}
                value={text}
                onChange={e => setText(e.target.value)}
                autoFocus={autoFocus}
                onKeyDown={e => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
                }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {onCancel && <Button size="small" onClick={onCancel}>Cancel</Button>}
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!text.trim() || submitting}
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                >
                    {submitting ? 'Posting...' : 'Post'}
                </Button>
            </Box>
        </Box>
    );
}

function CommentItem({ comment, currentUserId, isAdmin, recipeId, onDelete, onEdit, onReply, isReply = false }) {
    const [editing, setEditing] = useState(false);
    const [replying, setReplying] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const ts = formatTimestamp(comment.date_created, comment.edited_at);

    const isOwn = comment.user_id === currentUserId;
    const canDelete = isOwn || isAdmin;

    const handleEdit = async (newText) => {
        await onEdit(comment.id, newText);
        setEditing(false);
    };

    const handleReply = async (text) => {
        await onReply(text, comment.id);
        setReplying(false);
    };

    const handleDeleteConfirmed = async () => {
        setConfirmDelete(false);
        await onDelete(comment.id);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Avatar
                    src={comment.author.avatar_url || undefined}
                    sx={{ width: 32, height: 32, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'primary.main', flexShrink: 0, mt: 0.25 }}
                >
                    {!comment.author.avatar_url && authorInitials(comment.author)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                            {authorName(comment.author)}
                        </Typography>
                        {ts.tooltip ? (
                            <Tooltip title={ts.tooltip} placement="top">
                                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>
                                    {ts.display}
                                </Typography>
                            </Tooltip>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                {ts.display}
                            </Typography>
                        )}
                    </Box>

                    {editing ? (
                        <Box sx={{ mt: 0.5 }}>
                            <CommentInput
                                placeholder="Edit your comment..."
                                onSubmit={handleEdit}
                                onCancel={() => setEditing(false)}
                                autoFocus
                                initialText={comment.comment}
                            />
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {comment.comment}
                        </Typography>
                    )}

                    {!editing && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                            {!isReply && (
                                <Button
                                    size="small"
                                    startIcon={<ReplyIcon fontSize="small" />}
                                    onClick={() => setReplying(r => !r)}
                                    sx={{ minWidth: 0, px: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}
                                >
                                    Reply
                                </Button>
                            )}
                            {isOwn && (
                                <IconButton size="small" onClick={() => setEditing(true)} sx={{ color: 'text.secondary' }}>
                                    <EditIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            )}
                            {canDelete && (
                                <IconButton size="small" onClick={() => setConfirmDelete(true)} sx={{ color: 'text.secondary' }}>
                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                            )}
                        </Box>
                    )}

                    {replying && (
                        <Box sx={{ mt: 1 }}>
                            <CommentInput
                                placeholder={`Reply to ${authorName(comment.author)}...`}
                                onSubmit={handleReply}
                                onCancel={() => setReplying(false)}
                                autoFocus
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Replies — one level deep */}
            {comment.replies?.length > 0 && (
                <Box sx={{ ml: 5.5, mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[...comment.replies]
                        .sort((a, b) => new Date(a.date_created) - new Date(b.date_created))
                        .map(reply => (
                            <CommentItem
                                key={reply.id}
                                comment={reply}
                                currentUserId={currentUserId}
                                isAdmin={isAdmin}
                                recipeId={recipeId}
                                onDelete={onDelete}
                                onEdit={onEdit}
                                onReply={onReply}
                                isReply
                            />
                        ))}
                </Box>
            )}

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete comment?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">This can't be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmDelete(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function RecipeComments({ recipeId }) {
    const [comments, setComments] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalReplies, setTotalReplies] = useState(0);
    const [page, setPage] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
    const isAdmin = currentUser.role === 'admin';
    const headers = { Authorization: `Bearer ${Cookies.get('token')}`, 'Content-Type': 'application/json' };

    const fetchComments = useCallback(async (resetPage = true) => {
        try {
            const res = await fetch(`${API}/recipe/${recipeId}/comments?page=1`, { headers });
            if (res.ok) {
                const data = await res.json();
                setComments(data.comments);
                setTotal(data.total);
                setTotalReplies(data.total_replies);
                setPage(1);
            }
        } finally {
            setLoading(false);
        }
    }, [recipeId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchComments(); }, [fetchComments]);

    const handleLoadMore = async () => {
        setLoadingMore(true);
        try {
            const nextPage = page + 1;
            const res = await fetch(`${API}/recipe/${recipeId}/comments?page=${nextPage}`, { headers });
            if (res.ok) {
                const data = await res.json();
                setComments(prev => [...prev, ...data.comments]);
                setPage(nextPage);
            }
        } finally {
            setLoadingMore(false);
        }
    };

    const handlePost = async (text) => {
        const res = await fetch(`${API}/recipe/${recipeId}/comments`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ comment: text }),
        });
        if (res.ok) fetchComments();
    };

    const handleReply = async (text, parentId) => {
        const res = await fetch(`${API}/recipe/${recipeId}/comments`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ comment: text, parent_comment_id: parentId }),
        });
        if (res.ok) fetchComments();
    };

    const handleEdit = async (commentId, newText) => {
        const res = await fetch(`${API}/recipe/${recipeId}/comments/${commentId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ comment: newText }),
        });
        if (res.ok) fetchComments();
    };

    const handleDelete = async (commentId) => {
        const res = await fetch(`${API}/recipe/${recipeId}/comments/${commentId}`, {
            method: 'DELETE',
            headers,
        });
        if (res.ok) fetchComments();
    };

    const totalCount = total + totalReplies;
    const hasMore = comments.length < total;

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ChatBubbleOutlineIcon fontSize="small" color="action" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Comments {totalCount > 0 && `(${totalCount})`}
                </Typography>
            </Box>

            <CommentInput placeholder="Leave a comment..." onSubmit={handlePost} />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : comments.length > 0 ? (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {comments.map((comment, i) => (
                        <Box key={comment.id}>
                            <CommentItem
                                comment={comment}
                                currentUserId={currentUser.id}
                                isAdmin={isAdmin}
                                recipeId={recipeId}
                                onDelete={handleDelete}
                                onEdit={handleEdit}
                                onReply={handleReply}
                            />
                            {i < comments.length - 1 && <Divider sx={{ mt: 2.5 }} />}
                        </Box>
                    ))}
                    {hasMore && (
                        <Button
                            size="small"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            startIcon={loadingMore ? <CircularProgress size={14} color="inherit" /> : null}
                            sx={{ alignSelf: 'center', color: 'text.secondary' }}
                        >
                            {loadingMore ? 'Loading...' : `Load more (${total - comments.length} remaining)`}
                        </Button>
                    )}
                </Box>
            ) : null}
        </Box>
    );
}
